/**
 * Rota de entrega por urgência e proximidade.
 * Port das funções buildRoutes e orderStops do protótipo.
 */

const ROAD_FACTOR = 1.3
const MIN_ORDER_KG = 500
const MIN_TRUCK_KG = 5000
export const FILL_SILO_COVER = 99

export function parseOriginInput(value) {
  const parts = String(value || '').split(',').map((part) => parseFloat(part.trim()))
  if (parts.length !== 2 || parts.some((number) => !Number.isFinite(number))) return null
  if (Math.abs(parts[0]) > 90 || Math.abs(parts[1]) > 180) return null
  return parts
}

export function roadKm(from, to) {
  const earthRadiusKm = 6371
  const toRad = (degrees) => degrees * Math.PI / 180
  const dLat = toRad(to[0] - from[0])
  const dLng = toRad(to[1] - from[1])
  const haversine = Math.pow(Math.sin(dLat / 2), 2)
    + Math.cos(toRad(from[0])) * Math.cos(toRad(to[0])) * Math.pow(Math.sin(dLng / 2), 2)
  return 2 * earthRadiusKm * Math.asin(Math.sqrt(haversine)) * ROAD_FACTOR
}

export function tourLengthKm(origin, points) {
  let distance = 0
  let current = origin
  for (const point of points) {
    distance += roadKm(current, point)
    current = point
  }
  return distance + roadKm(current, origin)
}

export function orderStops(origin, stops) {
  const rest = [...stops]
  let sequence = []
  let current = origin
  while (rest.length) {
    rest.sort((a, b) => (
      roadKm(current, [a.row.lat, a.row.lng]) - roadKm(current, [b.row.lat, b.row.lng])
    ))
    const next = rest.shift()
    sequence.push(next)
    current = [next.row.lat, next.row.lng]
  }

  const pointsOf = (items) => items.map((item) => [item.row.lat, item.row.lng])
  let best = tourLengthKm(origin, pointsOf(sequence))
  let improved = true
  while (improved) {
    improved = false
    for (let start = 0; start < sequence.length - 1; start += 1) {
      for (let end = start + 1; end < sequence.length; end += 1) {
        const candidate = [
          ...sequence.slice(0, start),
          ...sequence.slice(start, end + 1).reverse(),
          ...sequence.slice(end + 1),
        ]
        const length = tourLengthKm(origin, pointsOf(candidate))
        if (length < best - 0.01) {
          best = length
          sequence = candidate
          improved = true
        }
      }
    }
  }
  return sequence
}

export function buildRoutes(rows, origin, horizon, cover, truckKg) {
  const truck = Math.max(MIN_TRUCK_KG, Number(truckKg) || 18000)
  const coverDays = Number(cover)
  const horizonDays = Number(horizon)
  if (!origin || origin.length !== 2) return []

  const candidates = (rows || [])
    .filter((row) => (
      row.daysToZero != null
      && row.daysToZero <= horizonDays
      && row.capacity
      && row.lat != null
      && row.lng != null
    ))
    .map((row) => {
      const free = Math.max(0, row.capacity - row.stock)
      const want = coverDays >= FILL_SILO_COVER ? free : Math.min(free, row.movingAvg * coverDays)
      return { row, need: Math.round(want / 100) * 100 }
    })
    .filter((candidate) => candidate.need >= MIN_ORDER_KG)
    .sort((a, b) => a.row.daysToZero - b.row.daysToZero)

  const trips = []
  for (const candidate of candidates) {
    while (candidate.need >= truck) {
      trips.push({
        stops: [{ row: candidate.row, load: truck }],
        full: true,
      })
      candidate.need -= truck
    }
  }

  const pool = candidates.filter((candidate) => candidate.need >= MIN_ORDER_KG)
  while (pool.length) {
    const seed = pool.shift()
    const stops = [{ row: seed.row, load: seed.need }]
    let load = seed.need
    for (;;) {
      const fits = pool.filter((candidate) => load + candidate.need <= truck)
      if (!fits.length) break
      fits.sort((a, b) => {
        const distanceTo = (candidate) => Math.min(...stops.map((stop) => (
          roadKm([stop.row.lat, stop.row.lng], [candidate.row.lat, candidate.row.lng])
        )))
        return distanceTo(a) - distanceTo(b)
      })
      const next = fits[0]
      stops.push({ row: next.row, load: next.need })
      load += next.need
      pool.splice(pool.indexOf(next), 1)
    }
    trips.push({ stops, full: false })
  }

  for (const trip of trips) {
    trip.stops = orderStops(origin, trip.stops)
    trip.load = trip.stops.reduce((sum, stop) => sum + stop.load, 0)
    trip.km = tourLengthKm(origin, trip.stops.map((stop) => [stop.row.lat, stop.row.lng]))
    trip.deadline = Math.min(...trip.stops.map((stop) => stop.row.daysToZero))
  }

  trips.sort((a, b) => a.deadline - b.deadline || a.km - b.km)
  return trips
}
